import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    OnInit,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { Event, NavigationEnd } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';

import { AbstractLayerProjectComponent } from '@connect-services/client/components/abstract-layer-project/abstract-layer-project.component';
import { FieldInterventionStatusComponent } from '@connect-services/client/components/field-intervention-status/field-intervention-status.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import {
    FieldInterventionEntitiesFilterService,
    FilterEntitiesDto,
} from '@connect-services/client/services/field-intervention-entities-filter.service';
import { FieldInterventionMapRendererService } from '@connect-services/client/services/field-intervention-map-renderer.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { SelectionService } from '@connect-services/client/services/selection.service';
import { ZoneDelimiterService } from '@connect-services/client/services/zone-delimiter.service';
import { CheckboxComponent } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { Deposit, FieldInterventionEntity, LayerAttribute, LayerWithAttributes } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        CustomTableComponent,
        FieldInterventionStatusComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
    ],
    selector: 'app-field-intervention-entities-list',
    styleUrl: './field-intervention-entities-list.component.scss',
    templateUrl: './field-intervention-entities-list.component.html',
})
export class FieldInterventionEntitiesListComponent extends AbstractLayerProjectComponent implements OnInit {
    public readonly canSelect = input(false);
    public readonly currentLayers: InputSignal<LayerWithAttributes[]> = input.required();
    public readonly deposits: WritableSignal<{ [key: string]: Deposit }> = signal({});
    public readonly tableColumns: Signal<CustomColumn[]> = computed(() => {
        if (!this.entitiesTableByLayerService.activeLayers().length) {
            return [];
        }

        const baseColumns: CustomColumn[] = [
            {
                key: 'businessIdentifier',
                label: 'businessIdentifier',
                translate: true,
            },
            {
                key: 'status',
                label: this.translocoService.translate('status', { COUNT: 1 }),
                translate: false,
            },
        ];

        const bookmarkedAttributesColumns: CustomColumn[] = this.entitiesTableByLayerService
            //all grouped layers will have the same bookmarked attributes
            .activeLayers()[0]
            .dataAttributes.filter((attribute: LayerAttribute) => attribute.isBookmarked)
            .map((attribute: LayerAttribute) => {
                return {
                    key: attribute.column,
                    label: attribute.column,
                };
            });

        return [...baseColumns, ...bookmarkedAttributesColumns];
    });

    protected readonly currentPanelEntity: WritableSignal<string | null> = signal(null);
    protected entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    protected fieldInterventionService = inject(FieldInterventionService);
    protected selectionService = inject(SelectionService<FieldInterventionEntity>);

    private readonly fieldInterventionEntitiesFilterService = inject(FieldInterventionEntitiesFilterService);
    private readonly fieldInterventionMapRendererService = inject(FieldInterventionMapRendererService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly translocoService = inject(TranslocoService);
    private readonly zoneDelimiterService = inject(ZoneDelimiterService);

    public constructor() {
        super();
        this.destroyRef.onDestroy(() => {
            this.selectionService.reset();
        });
        this.currentPanelEntity.set(this.fieldInterventionService.getDetailPanelUuidFromUrl());

        effect(async (): Promise<void> => {
            const filter: FilterEntitiesDto | undefined =
                this.fieldInterventionEntitiesFilterService.baseFilterParams();

            await untracked((): Promise<void> =>
                this.mapFieldInterventionService.filterFieldInterventionEntitiesOnMap(
                    this.currentLayers().map((layer: LayerWithAttributes): string => layer.id),
                    this.entitiesTableByLayerService.currentSources(),
                    filter,
                ),
            );
        });

        effect(async (): Promise<void> => {
            if (!this.entitiesTableByLayerService.isZoneDelimiterFilterActive()) {
                return;
            }

            await untracked(() => this.applyFilter());
        });

        this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((): void =>
                this.currentPanelEntity.set(this.fieldInterventionService.getDetailPanelUuidFromUrl()),
            );
    }

    public async ngOnInit(): Promise<void> {
        this.entitiesTableByLayerService.isSourceDrawn.set(false);
        await this.initEntitiesListAndSources();
    }

    public onSortChange(sort?: SortState): void {
        this.entitiesTableByLayerService.resetPagination();
        this.entitiesTableByLayerService.onSortChange(sort);
    }

    private async applyFilter(): Promise<void> {
        const selectedGeom = this.zoneDelimiterService.getZoneDelimiter();
        const drawPolygon = this.zoneDelimiterService.getZoneDelimiterFromDrawPolygons();

        this.entitiesTableByLayerService.zoneDelimiter.set(selectedGeom ?? drawPolygon);
        await this.entitiesTableByLayerService.fetchEntities();
    }

    private async initEntitiesListAndSources(): Promise<void> {
        this.mapFieldInterventionService.resetFilter();

        this.entitiesTableByLayerService.activeLayers.set(this.currentLayers());

        await this.entitiesTableByLayerService.resetTableData();

        await this.entitiesTableByLayerService.setCurrentSourcesAndEntityType();
        await this.fieldInterventionMapRendererService.drawSourcesWithAreaMask(
            this.entitiesTableByLayerService.currentSources(),
        );

        await this.mapFieldInterventionService.filterFieldInterventionEntitiesOnMap(
            this.currentLayers().map((layer) => layer.id),
            this.entitiesTableByLayerService.currentSources(),
            this.fieldInterventionEntitiesFilterService.filterByParams(),
        );

        this.entitiesTableByLayerService.manageHighlight();
        this.entitiesTableByLayerService.setTotalItemsByLayer();
    }
}
